import { PayPolicy, PayPolicyListItem, PolicyVersionItem } from '../types/Payroll/PayPolicy'
import { ProjectHoliday, ProjectHolidayRequest } from '../types/Payroll/ProjectHoliday'
import {
	postWithResponse,
	post,
	put,
	patch,
	deleteOne,
	getAll,
	getOne,
} from './CommonServices/httpService'
import { AllowanceResponse, AllowanceUpsertRequest } from '../types/Payroll/Allowances'
import { PayCodeResponse, PayCodeUpsertRequest } from '../types/Payroll/PayCodes'
import { TimeCodeResponse, TimeCodeUpsertRequest } from '../types/Payroll/TimeCodes'
//#region Pay Policies
export const publishPayPolicy = async (policy: PayPolicy) => {
	const response = await post('/paypolicy', policy)
	return response
}

export const getPayPolicies = async (excludeArchived = false): Promise<PayPolicyListItem[]> => {
	const response = await getAll<PayPolicyListItem[]>(
		'/paypolicy?excludeArchived=' + excludeArchived,
	)
	return response.success ? response.data : []
}

export const getProjectHolidays = async (): Promise<ProjectHoliday[]> => {
	try {
		const res = await getAll<ProjectHoliday[]>('/PayPolicy/Holidays')
		return res.success ? res.data : []
	} catch {
		return []
	}
}

export const getPayPolicyConditions = async (id: number): Promise<PayPolicy> => {
	const response = await getOne<PayPolicy>('/paypolicy/' + id)
	return response.data
}

export const createProjectHoliday = async (
	request: ProjectHolidayRequest,
): Promise<ProjectHoliday> => {
	const res = await postWithResponse<ProjectHoliday>('/PayPolicy/Holidays', request)
	if (!res.success || !res.data) throw new Error('Failed to create holiday')
	return res.data
}

export const updateProjectHoliday = async (
	id: number,
	request: ProjectHolidayRequest,
): Promise<ProjectHoliday> => {
	await put(`/PayPolicy/Holidays/${id}`, request)
	return { id, name: request.name, startDate: request.startDate, endDate: request.endDate }
}

export const deleteProjectHoliday = async (id: number): Promise<void> => {
	await deleteOne(`/PayPolicy/Holidays/${id}`)
}

export const archivePayPolicy = async (id: number): Promise<void> => {
	await post(`/PayPolicy/${id}/Archive`, {})
}

export const unarchivePayPolicy = async (id: number): Promise<void> => {
	await post(`/PayPolicy/${id}/Unarchive`, {})
}

export const getPolicyVersions = async (policyId: number): Promise<PolicyVersionItem[]> => {
	const res = await getAll<PolicyVersionItem[]>(`/PayPolicy/${policyId}/Versions`)
	return res.success ? res.data : []
}

export const activatePolicyVersion = async (policyId: number): Promise<void> => {
	await post(`/PayPolicy/${policyId}/Activate`, {})
}
//#endregion

//#region Allowances
export const getAllowances = async (): Promise<AllowanceResponse[]> => {
	const res = await getAll<AllowanceResponse[]>(`/Allowance`)
	return res.success ? res.data : []
}

export const createAllowance = async (
	request: AllowanceUpsertRequest,
): Promise<void> => {
	await post('/Allowance', request)
}

export const getAllowance = async (id: number): Promise<AllowanceResponse> => {
	const res = await getOne<AllowanceResponse>(`/Allowance/${id}`)
	if (!res.success || !res.data) throw new Error('Failed to fetch allowance')
	return res.data
}

export const updateAllowance = async (
	id: number,
	request: AllowanceUpsertRequest,
): Promise<void> => {
	await put(`/Allowance/${id}`, request)
}

export const deleteAllowance = async (id: number): Promise<void> => {
	await deleteOne(`/Allowance/${id}`)
}

//#endregion

//#region Pay Codes
export const getPayCodes = async (): Promise<PayCodeResponse[]> => {
	const res = await getAll<PayCodeResponse[]>(`/PayCode`)
	return res.success ? res.data : []
}

export const createPayCodes = async (
	requests: PayCodeUpsertRequest[],
): Promise<PayCodeResponse[]> => {
	const res = await postWithResponse<PayCodeResponse[]>(`/PayCode`, requests)
	if (!res.success || !res.data) throw new Error('Failed to create pay code')
	return res.data
}

export const updatePayCode = async (id: number, request: PayCodeUpsertRequest): Promise<void> => {
	await put(`/PayCode/${id}`, request)
}

export const deletePayCode = async (id: number): Promise<void> => {
	await deleteOne(`/PayCode/${id}`)
}

export const assignPayCodeToEmployees = async (
	payCodeId: number,
	workerIds: number[],
): Promise<void> => {
	await patch(`/PayCode/${payCodeId}/employees`, workerIds)
}
//#endregion

//#region Time Codes
export const getTimeCodes = async (): Promise<TimeCodeResponse[]> => {
	const res = await getAll<TimeCodeResponse[]>(`/PayCode/timecodes`)
	return res.success ? res.data : []
}

export const createTimeCode = async (
	request: TimeCodeUpsertRequest,
): Promise<TimeCodeResponse> => {
	const res = await postWithResponse<TimeCodeResponse>(`/PayCode/timecodes`, request)
	if (!res.success || !res.data) throw new Error('Failed to create time code')
	return res.data
}

export const updateTimeCode = async (
	id: number,
	request: TimeCodeUpsertRequest,
): Promise<void> => {
	await put(`/PayCode/timecodes/${id}`, request)
}

export const deleteTimeCode = async (id: number): Promise<void> => {
	await deleteOne(`/PayCode/timecodes/${id}`)
}
//#endregion
